import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  GetQuotationsResponse,
  QuotationDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  HttpGetMetaData,
  InternalAPI,
} from 'services/APIs/InternalAPI/InternalAPI'
import { GetContext } from 'store/storeUtils'

interface IQuotationsAPI {
  getQuotations: (
    meta: HttpGetMetaData<QuotationDto>,
  ) => Promise<GetQuotationsResponse>
  downloadQuotation: (quotationId: string) => Promise<boolean>
  deleteQuotation: (quotationId: string) => Promise<void>
  postQuotationResponseToSCSN: (
    projectId: string,
    forwardSender: string,
    forwardId: string,
  ) => Promise<void>
}


class QuotationsAPI extends InternalAPI implements IQuotationsAPI {
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(`/api/parties/${partyId}/quotations`, onRequestChange)
  }

  public async getQuotations(
    meta: HttpGetMetaData<QuotationDto>,
  ): Promise<GetQuotationsResponse> {
    return this.GetPagedAsync<GetQuotationsResponse>({
      relativePath: '',
      _meta: meta,
      id: 'GetQuotationsList',
    })
  }

  public async downloadQuotation(quotationId: string): Promise<boolean> {
    return await this.DownloadAsync({
      relativePath: `/${quotationId}/DownloadUbl`,
      id: `DownloadQuotation-${quotationId}`,
    })
  }

  public async deleteQuotation(quotationId: string): Promise<void> {
    return await this.DeleteAsync({
      relativePath: `/${quotationId}`,
      id: `DeleteQuotation-${quotationId}`,
    })
  }

  public async postQuotationResponseToSCSN(
    projectId: string,
    forwardSender: string,
    forwardId: string,
  ): Promise<void> {
    return await this.PostAsync({
      relativePath: '/postQuotationResponseToSCSN',
      id: `PostQuotationResponseToSCSN-${projectId}`,
      data: { projectId, forwardSender, forwardId },
    })
  }
}

export class QuotationsController extends BaseController<IQuotationsAPI> {
  constructor() {
    super((onRequestChange) => {
      const { partyId } = GetContext()
      return new QuotationsAPI(partyId, onRequestChange)
    })
  }

  public async getQuotations(
    meta: HttpGetMetaData<QuotationDto>,
  ): Promise<GetQuotationsResponse> {
    try {
      return await this.api.getQuotations(meta)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async downloadQuotation(quotationId: string): Promise<boolean> {
    try {
      return await this.api.downloadQuotation(quotationId)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async deleteQuotation(quotationId: string): Promise<void> {
    try {
      return await this.api.deleteQuotation(quotationId)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async PostQuotationResponseToSCSN(
    forwardId: string,
    forwardSender: string,
    projectId: string,
  ): Promise<void> {
    try {
      return await this.api.postQuotationResponseToSCSN(
        forwardId,
        forwardSender,
        projectId,
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }
}
